import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, AlertController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';

import { EditKnowledgeBaseItemComponent } from 'src/app/components/edit-knowledge-base-item/edit-knowledge-base-item.component';
import { FEATURES } from 'src/app/constants';
import { KnowledgeBaseScrapeJob } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { KnowledgeBaseScrapeJobsService } from 'src/services/knowledge-base-scrape-jobs.service';
import { MessageService } from 'src/services/message.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-knowledge-base-scrape-jobs',
  templateUrl: './knowledge-base-scrape-jobs.component.html',
  styleUrls: ['./knowledge-base-scrape-jobs.component.scss'],
})
export class KnowledgeBaseScrapeJobsComponent implements OnInit {
  knowledgeBaseScrapeJobs: Partial<KnowledgeBaseScrapeJob>[] = [];
  filteredKnowledgeBaseScrapeJobs: Partial<KnowledgeBaseScrapeJob>[] = [];

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  featureEnabled = true;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private knowledgeBaseScrapeJobsService: KnowledgeBaseScrapeJobsService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
  ) {}

  async ngOnInit() {
    this.featureEnabled = !this.authService?.restrictedFeatures?.includes(FEATURES.KNOWLEDGE_BASE_SYNC);
    this.isLoading = true;

    await Promise.all([this.getKnowledgeBaseScrapeJobs()]);

    this.isLoading = false;
  }

  async getKnowledgeBaseScrapeJobs() {
    try {
      const knowledgeBaseScrapeJobsResponse = await this.knowledgeBaseScrapeJobsService.list({
        limit: null,
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.authService.selectedProject?.id ?? null,
          },
        ],
      });

      this.knowledgeBaseScrapeJobs = knowledgeBaseScrapeJobsResponse.data;
      this.filteredKnowledgeBaseScrapeJobs = this.knowledgeBaseScrapeJobs;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredKnowledgeBaseScrapeJobs = this.knowledgeBaseScrapeJobs;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredKnowledgeBaseScrapeJobs = this.knowledgeBaseScrapeJobs.filter((knowledgeBaseScrapeJob) =>
        knowledgeBaseScrapeJob.startUrl.toLowerCase().includes(term),
      );
    }
  }

  async editKnowledgeBaseScrapeJob(id?: string) {
    const modal = await this.modalCtrl.create({
      component: EditKnowledgeBaseItemComponent,
      componentProps: {
        id,
        type: 'scrape-job',
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getKnowledgeBaseScrapeJobs();
    });
  }

  async runKnowledgeBaseScrapeJob(knowledgeBaseScrapeJob: KnowledgeBaseScrapeJob) {
    // Hack to show the loading indicator
    knowledgeBaseScrapeJob.apifyRunStatus = 'RUNNING';

    try {
      await this.knowledgeBaseScrapeJobsService.create({
        id: knowledgeBaseScrapeJob.id,
        startUrl: knowledgeBaseScrapeJob.startUrl,
        projectId: knowledgeBaseScrapeJob.projectId,
      });
      this.msgSrvc.show('New syncing job started; please wait a few minutes to complete');
    } catch (e) {
      knowledgeBaseScrapeJob.apifyRunStatus = 'FAILED';
      this.msgSrvc.show('Error while attempting to run sync job');
    }
  }

  isJobSyncing(job: KnowledgeBaseScrapeJob) {
    return (
      !job.apifyRunStatus || job.apifyRunStatus === 'RUNNING' || (job.apifyRunStatus === 'READY' && !job.lastRunAt)
    );
  }

  async showErrorDetails(job: KnowledgeBaseScrapeJob) {
    const alert = await this.alertCtrl.create({
      header: `Error`,
      subHeader: job.result?.error?.message ?? job.result?.error ?? 'Unknown error',
    });

    await alert.present();
  }

  async addKnowledgeBaseSource() {
    const modal = await this.modalCtrl.create({
      component: EditKnowledgeBaseItemComponent,
      backdropDismiss: false,
      componentProps: {
        type: 'scrape-job',
      },
    });
    await modal.present();
    modal.onDidDismiss().then(() => {
      this.getKnowledgeBaseScrapeJobs();
    });
  }
}
